import { fetchApiGraphQL } from '@/utils/fetcher';
import { hasValidToken } from './tokenstore';
import {
    getUserAffiliationsMutation,
    type GetUserAffiliationsResult
} from '../Affiliation/affiliation.query';

const storage = localStorage;
const storageKey = 'affiliations';

/* Resolves once the cache reflects the current session. The member gate reads
   affiliations synchronously and only once per page, so anything that decides
   member vs non-member has to await this rather than race the fetch. */
let ready: Promise<void> | null = null;

// Starts the fetch itself if nothing has yet, so callers never depend on having
// been initialised after initUsers() in the entrypoint.
export function whenUserAffiliationsReady(): Promise<void> {
    return ready ?? refreshUserAffiliations();
}

export function getUserAffiliations(): string[] {
    if (!hasValidToken()) {
        return [];
    }

    try {
        const json = storage.getItem(storageKey);
        return json ? JSON.parse(json) as string[] : [];
    } catch {
        return [];
    }
}

export function setUserAffiliations(affiliationTypes: string[] | null) {
    if (!affiliationTypes) {
        storage.removeItem(storageKey);
        return;
    }

    storage.setItem(storageKey, JSON.stringify(affiliationTypes));
}

export function refreshUserAffiliations(): Promise<void> {
    if (!hasValidToken()) {
        ready = Promise.resolve();
        return ready;
    }

    ready = fetchApiGraphQL<GetUserAffiliationsResult>(getUserAffiliationsMutation, {})
        .then(result => {
            const affiliations = result?.data?.getUserAffiliations ?? [];
            setUserAffiliations(affiliations.map(a => a.affiliationType));
        })
        /* A failed fetch is not the same answer as "no affiliations" - keep what
           the cache already holds instead of silently demoting a member. */
        .catch(() => { });

    return ready;
}

export default function init() {
    void refreshUserAffiliations();
}
